import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { DayPicker } from "react-day-picker";
import { useTranslation } from "react-i18next";

import { buttonVariants } from "@/components/ui/button";
import { getWeekdayHeaderNames, resolveDateFnsLocale } from "@/lib/date-locale";
import { cn } from "@/lib/utils";

export type TCalendarProps = React.ComponentProps<typeof DayPicker> & {
  shouldHighlightToday?: boolean;
};

const CalendarIconLeft = (): React.ReactElement => (
  <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
);

const CalendarIconRight = (): React.ReactElement => (
  <ChevronRight className="h-4 w-4 rtl:rotate-180" />
);

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  shouldHighlightToday = true,
  ...props
}: TCalendarProps) {
  const { i18n } = useTranslation();

  const appLocale = React.useMemo(
    () => resolveDateFnsLocale(i18n.language),
    [i18n.language],
  );

  // NOTE: A caller may override the locale, and the weekday header has to be
  // built from the SAME one — deriving it from `i18n.language` regardless would
  // print the caption and the header row in two different languages.
  const locale = props.locale ?? appLocale;

  const weekdayNames = React.useMemo(
    () => getWeekdayHeaderNames(locale),
    [locale],
  );

  const formatWeekdayName = React.useCallback(
    (weekday: Date): string => weekdayNames[weekday.getDay()],
    [weekdayNames],
  );

  // NOTE: Every react-day-picker surface in the app renders through this one
  // component, so handing it the locale here is what localises the month
  // caption, the weekday header and the first day of the week everywhere at
  // once — the grid used to be English regardless of the app language
  // (NSAI-4989). Both sit after the spread: `locale` already folds in a
  // caller's override, and `formatters` merges rather than replaces so a
  // caller adding one formatter cannot silently drop the weekday header back
  // to English.
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100",
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: shouldHighlightToday
          ? "bg-accent text-accent-foreground"
          : "",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: CalendarIconLeft,
        IconRight: CalendarIconRight,
      }}
      {...props}
      locale={locale}
      formatters={{ formatWeekdayName, ...props.formatters }}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
